'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { BreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BreadcrumbsFragmentFragmentDoc';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useState } from 'react';
import { unmask } from '@packages/gql/src/betterMasking';
import {
  Breadcrumbs,
  BreadcrumbsEllipsis,
  BreadcrumbsItem,
  BreadcrumbsLink,
  BreadcrumbsList,
} from '@packages/shared/src/ui/Breadcrumbs/Breadcrumbs';
import { Link } from '@packages/modules/src/Link/Link';
import { useIntl } from 'react-intl';
import { ExtendedBreadcrumbsBackButton } from './ExtendedBreadcrumbsBackButton/ExtendedBreadcrumbsBackButton.client';
import { BreadcrumbsWrapper } from './BreadcrumbsWrapper/BreadcrumbsWrapper';

/* GraphQL */ `
  fragment BreadcrumbsFragment on Breadcrumb {
    breadCrumb {
      name
      id
      uriPath
    }
  }
`;

export type ExtendedBreadcrumbsProps = {
  /** The breadcrumbs object. */
  breadCrumbData: FragmentType<typeof BreadcrumbsFragmentFragmentDoc> | undefined | null;
};

/* 
  This component renders a breadcrumbs navigation

  mobile: there is only a button visible that links to the last item of the breadcrumbs data
  tablet: a back button and a collapsed breadcrumbs is visible
  desktop: a back button and a extended breadcrumbs is visible
*/
export const ExtendedBreadcrumbs = ({ breadCrumbData }: ExtendedBreadcrumbsProps) => {
  const {
    host: { domain },
  } = useConfig();
  const { formatMessage } = useIntl();
  const [forceExpanded, setForceExpanded] = useState(false);

  const breadCrumb = breadCrumbData
    ? (unmask<typeof BreadcrumbsFragmentFragmentDoc>(breadCrumbData).breadCrumb ?? [])
    : [];
  return (
    <BreadcrumbsWrapper>
      <div className="flex flex-row items-center gap-1">
        <ExtendedBreadcrumbsBackButton
          name={breadCrumb[breadCrumb.length - 1]?.name}
          uriPath={breadCrumb[breadCrumb.length - 1]?.uriPath}
        />

        {breadCrumb.length > 0 && (
          <>
            <div className="hidden w-full md:block">
              <Breadcrumbs>
                <BreadcrumbsList>
                  {breadCrumb.map(({ name, uriPath, id }, index) => {
                    const isBetweenFirstAndLast = index > 0 && index < breadCrumb.length - 1;
                    const isSecondLast = index === breadCrumb.length - 2;
                    const isBetweenFirstAndSecondLast = index > 0 && index < breadCrumb.length - 2;

                    const seperatorClasses =
                      'last:text-color-text-darktransparent last:font-normal not-last:after:pl-1 not-last:after:content-["/"] whitespace-nowrap';

                    return (
                      <BreadcrumbsItem
                        className={`${seperatorClasses} ${
                          isBetweenFirstAndSecondLast && !forceExpanded ? 'max-lg:hidden' : ''
                        }`}
                        key={id}
                      >
                        <BreadcrumbsLink
                          asChild
                          className={isBetweenFirstAndLast && !forceExpanded ? 'max-lg:hidden' : ''}
                        >
                          <Link href={uriPath}>{name}</Link>
                        </BreadcrumbsLink>
                        {isSecondLast && !forceExpanded && (
                          <div className="block lg:hidden" aria-hidden>
                            <BreadcrumbsEllipsis
                              aria-label={formatMessage({
                                id: 'extendedBreadcrumbs.ellipsis.ariaLabel',
                                defaultMessage: 'Pfad anzeigen',
                              })}
                              onClick={() => setForceExpanded(true)}
                            />
                          </div>
                        )}
                      </BreadcrumbsItem>
                    );
                  })}
                </BreadcrumbsList>
              </Breadcrumbs>
            </div>
            <script
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  itemListElement: breadCrumb.map(({ name, uriPath }, index) => ({
                    item: { name, '@id': `https://www.${domain}${uriPath}` },
                    '@type': 'ListItem',
                    position: index + 1,
                  })),
                  '@type': 'BreadcrumbList',
                  '@context': 'http://schema.org',
                }),
              }}
            />
          </>
        )}
      </div>
    </BreadcrumbsWrapper>
  );
};
